import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { brandKitAPI } from '../lib/api';
import { AtSign } from 'lucide-react';
import {
  Palette, ImageIcon, Type, LayoutTemplate, Sparkles, FileText, Ban,
  Plus, Trash2, Upload, Download, Copy, Check, ExternalLink, FolderOpen,
  Link2, Loader2, Save, Lock, Pipette,
} from 'lucide-react';

const uid = () => Math.random().toString(36).slice(2, 10);
const isImg = (f) => f && ((f.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name || ''));
const isPdf = (f) => f && ((f.content_type || '') === 'application/pdf' || /\.pdf$/i.test(f.name || ''));

function FilePreview({ file, className }) {
  if (!file) return null;
  if (isImg(file)) {
    return <img src={brandKitAPI.fileUrl(file.id)} alt={file.name} className={className || 'rounded-md border object-contain bg-white h-24 w-full'} />;
  }
  return (
    <div className={`flex items-center justify-center rounded-md border bg-muted/40 ${className || 'h-24 w-full'}`}>
      <FileText className="w-8 h-8 text-muted-foreground" />
    </div>
  );
}

function DownloadBtn({ file }) {
  if (!file) return null;
  return (
    <a href={brandKitAPI.fileUrl(file.id)} target="_blank" rel="noreferrer" download={file.name} className="inline-flex" data-testid={`bk-download-${file.id}`}>
      <Button type="button" variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> {file.name?.length > 22 ? file.name.slice(0, 20) + '…' : file.name}</Button>
    </a>
  );
}

export default function BrandKitSection({ clientId }) {
  const [kit, setKit] = useState(null);
  const [briefingFiles, setBriefingFiles] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copiedHex, setCopiedHex] = useState('');
  const [uploadingKey, setUploadingKey] = useState('');
  const [extracting, setExtracting] = useState(false);
  const hiddenInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brandKitAPI.get(clientId);
      setKit(res.data);
      setBriefingFiles(res.briefing_files || []);
      setCanEdit(!!res.can_edit);
    } catch { toast.error('Error cargando el kit de marca'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const update = (patch) => { setKit((k) => ({ ...k, ...patch })); setDirty(true); };

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      const res = await brandKitAPI.save(clientId, next);
      setKit(res.data); setDirty(false);
      return true;
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); return false; }
    finally { setSaving(false); }
  }, [clientId]);

  const handleSave = async () => { if (await persist(kit)) toast.success('Kit de marca guardado'); };

  // Upload helper: uploads file, calls onDone(fileRef) with the persisted (whole) kit computed by caller
  const doUpload = async (key, file, applyToKit) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const rec = await brandKitAPI.uploadFile(clientId, file);
      const ref = { id: rec.id, name: rec.name, content_type: rec.content_type };
      const next = applyToKit(ref);
      await persist(next); // persist immediately so uploads are never lost
      toast.success('Archivo subido');
    } catch (e) { toast.error(e.message || 'Error subiendo archivo'); }
    finally { setUploadingKey(''); }
  };

  const appendColors = (hexes, baseKit) => {
    const bk = baseKit || kit;
    const existing = new Set((bk.colores || []).map((c) => (c.hex || '').toUpperCase()));
    const added = [];
    (hexes || []).forEach((h) => {
      const up = (h || '').toUpperCase();
      if (up && !existing.has(up)) { existing.add(up); added.push({ id: uid(), nombre: '', hex: up }); }
    });
    return { next: { ...bk, colores: [...(bk.colores || []), ...added] }, count: added.length };
  };

  const extractFromImage = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const { colors } = await brandKitAPI.extractColors(clientId, file);
      const { next, count } = appendColors(colors);
      if (count === 0) toast.info('Esos colores ya están en la paleta');
      else { await persist(next); toast.success(`${count} color(es) añadidos a la paleta`); }
    } catch (e) { toast.error(e.message || 'No se pudieron extraer colores'); }
    finally { setExtracting(false); }
  };

  const uploadCanvaCaptura = async (canvaId, file) => {
    if (!file) return;
    setUploadingKey(`canva-${canvaId}`);
    try {
      const rec = await brandKitAPI.uploadFile(clientId, file);
      const ref = { id: rec.id, name: rec.name, content_type: rec.content_type };
      let next = { ...kit, canva_links: kit.canva_links.map((x) => x.id === canvaId ? { ...x, captura: ref } : x) };
      let added = 0;
      try {
        const { colors } = await brandKitAPI.extractColors(clientId, file);
        const r = appendColors(colors, next); next = r.next; added = r.count;
      } catch (_) { /* extraction best-effort */ }
      await persist(next);
      toast.success(added > 0 ? `Captura subida · ${added} color(es) añadidos a la paleta` : 'Captura subida');
    } catch (e) { toast.error(e.message || 'Error subiendo captura'); }
    finally { setUploadingKey(''); }
  };

  const copyHex = (hex) => {
    if (!hex) return;
    navigator.clipboard?.writeText(hex).then(() => {
      setCopiedHex(hex); toast.success(`${hex} copiado`);
      setTimeout(() => setCopiedHex(''), 1200);
    }).catch(() => toast.error('No se pudo copiar'));
  };

  const pick = (key) => hiddenInputs.current[key]?.click();

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!kit) return null;

  const RO = !canEdit; // read only

  // ---- Section helpers ----
  const addLogo = () => update({ logos: [...kit.logos, { id: uid(), nombre: 'Nuevo logo', files: [] }] });
  const setLogo = (id, patch) => update({ logos: kit.logos.map((l) => l.id === id ? { ...l, ...patch } : l) });
  const delLogo = (id) => { const next = { ...kit, logos: kit.logos.filter((l) => l.id !== id) }; setKit(next); persist(next); };
  const addLogoFile = (logoId, ref) => ({ ...kit, logos: kit.logos.map((l) => l.id === logoId ? { ...l, files: [...(l.files || []), ref] } : l) });
  const delLogoFile = (logoId, fileId) => { const next = { ...kit, logos: kit.logos.map((l) => l.id === logoId ? { ...l, files: l.files.filter((f) => f.id !== fileId) } : l) }; setKit(next); persist(next); };

  const addColor = () => update({ colores: [...kit.colores, { id: uid(), nombre: '', hex: '#000000' }] });
  const setColor = (id, patch) => update({ colores: kit.colores.map((c) => c.id === id ? { ...c, ...patch } : c) });
  const delColor = (id) => update({ colores: kit.colores.filter((c) => c.id !== id) });

  const addTipo = () => update({ tipografias: [...kit.tipografias, { id: uid(), nombre: '', uso: '', url: '', file: null }] });
  const setTipo = (id, patch) => update({ tipografias: kit.tipografias.map((t) => t.id === id ? { ...t, ...patch } : t) });
  const delTipo = (id) => update({ tipografias: kit.tipografias.filter((t) => t.id !== id) });

  const addCanva = () => update({ canva_links: [...kit.canva_links, { id: uid(), nombre: '', url: '', captura: null }] });
  const setCanva = (id, patch) => update({ canva_links: kit.canva_links.map((c) => c.id === id ? { ...c, ...patch } : c) });
  const delCanva = (id) => update({ canva_links: kit.canva_links.filter((c) => c.id !== id) });

  const delEjemplo = (id) => { const next = { ...kit, ejemplos: kit.ejemplos.filter((e) => e.id !== id) }; setKit(next); persist(next); };
  const setEjemplo = (id, patch) => update({ ejemplos: kit.ejemplos.map((e) => e.id === id ? { ...e, ...patch } : e) });

  const delUsoImg = (fid) => { const next = { ...kit, usos_incorrectos: { ...kit.usos_incorrectos, imagenes: kit.usos_incorrectos.imagenes.filter((f) => f.id !== fid) } }; setKit(next); persist(next); };

  const SectionCard = ({ icon: Icon, title, desc, children, action }) => (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Icon className="w-4 h-4 text-primary" /> {title}</CardTitle>
            {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="brand-kit-section">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5 text-primary" /> Branding / Kit de Marca</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Todo lo que el equipo necesita para diseñar, editar y redactar sin preguntar.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {kit.drive_link ? (
                <a href={kit.drive_link} target="_blank" rel="noreferrer" data-testid="bk-drive-link">
                  <Button variant="outline" size="sm"><FolderOpen className="w-4 h-4 mr-1" /> Carpeta Drive <ExternalLink className="w-3 h-3 ml-1" /></Button>
                </a>
              ) : null}
              {RO ? (
                <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge>
              ) : (
                <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="bk-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {dirty ? 'Guardar cambios' : 'Guardado'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {!RO && (
          <CardContent className="pt-0">
            <Label className="text-xs flex items-center gap-1"><Link2 className="w-3 h-3" /> Enlace a la carpeta "BRANDING" de Google Drive</Label>
            <Input value={kit.drive_link || ''} onChange={(e) => update({ drive_link: e.target.value })} onBlur={() => dirty && persist(kit)} placeholder="https://drive.google.com/…" className="mt-1" data-testid="bk-drive-input" />
            <Label className="text-xs flex items-center gap-1 mt-3"><AtSign className="w-3 h-3" /> Usuario de Instagram (se usa en la vista previa de las publicaciones)</Label>
            <div className="relative mt-1 max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
              <Input value={kit.instagram_user || ''} onChange={(e) => update({ instagram_user: e.target.value.replace(/^@+/, '').replace(/\s/g, '') })} onBlur={() => dirty && persist(kit)} placeholder="opticacorella" className="pl-7" data-testid="bk-instagram-input" />
            </div>
          </CardContent>
        )}
        {RO && kit.instagram_user && (
          <CardContent className="pt-0"><p className="text-sm flex items-center gap-1" data-testid="bk-instagram-readonly"><AtSign className="w-3.5 h-3.5" /> Instagram: <b>@{kit.instagram_user}</b></p></CardContent>
        )}
      </Card>

      {/* Briefing auto-fill hint */}
      {briefingFiles.length > 0 && (
        <div className="rounded-lg border border-dashed p-4 bg-muted/30" data-testid="bk-briefing-files">
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Archivos que el cliente subió en su briefing (Branding)</p>
          <div className="flex flex-wrap gap-2">
            {briefingFiles.map((f) => (
              <a key={f.id} href={brandKitAPI.fileUrl(f.id)} target="_blank" rel="noreferrer" className="inline-flex">
                <Button type="button" variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> {f.name}</Button>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* 1) LOGOS */}
      <SectionCard icon={ImageIcon} title="1) Logos" desc="Versiones del logo (principal, negativo/blanco, isotipo…). Admite PNG, JPG, SVG, AI/PDF."
        action={!RO && <Button size="sm" variant="outline" onClick={addLogo} data-testid="bk-add-logo"><Plus className="w-4 h-4 mr-1" /> Añadir logo</Button>}>
        {kit.logos.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin logos.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kit.logos.map((l) => (
              <div key={l.id} className="rounded-lg border p-3 space-y-2" data-testid={`bk-logo-${l.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={l.nombre} onChange={(e) => setLogo(l.id, { nombre: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} className="h-8 font-medium" placeholder="Nombre de la versión" />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => delLogo(l.id)} data-testid={`bk-del-logo-${l.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(l.files || []).map((f) => (
                    <div key={f.id} className="space-y-1">
                      <FilePreview file={f} />
                      <div className="flex items-center gap-1">
                        <DownloadBtn file={f} />
                        {!RO && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => delLogoFile(l.id, f.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
                {!RO && (
                  <>
                    <input ref={(el) => (hiddenInputs.current[`logo-${l.id}`] = el)} type="file" className="hidden" onChange={(e) => doUpload(`logo-${l.id}`, e.target.files?.[0], (ref) => addLogoFile(l.id, ref))} data-testid={`bk-logo-file-${l.id}`} />
                    <Button type="button" variant="outline" size="sm" onClick={() => pick(`logo-${l.id}`)} disabled={uploadingKey === `logo-${l.id}`}>
                      {uploadingKey === `logo-${l.id}` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Añadir formato
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 2) COLORES */}
      <SectionCard icon={Palette} title="2) Colores" desc="Paleta de la marca. Haz clic en el HEX para copiarlo. Puedes extraer los colores automáticamente de una imagen (brand board, post o captura de Canva)."
        action={!RO && (
          <div className="flex items-center gap-2">
            <input ref={(el) => (hiddenInputs.current['extract-colors'] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { extractFromImage(e.target.files?.[0]); e.target.value = ''; }} data-testid="bk-extract-colors-file" />
            <Button size="sm" variant="outline" onClick={() => pick('extract-colors')} disabled={extracting} data-testid="bk-extract-colors-btn">
              {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pipette className="w-4 h-4 mr-1" />} Extraer de imagen
            </Button>
            <Button size="sm" variant="outline" onClick={addColor} data-testid="bk-add-color"><Plus className="w-4 h-4 mr-1" /> Añadir color</Button>
          </div>
        )}>
        {kit.colores.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin colores.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {kit.colores.map((c) => (
              <div key={c.id} className="rounded-lg border overflow-hidden" data-testid={`bk-color-${c.id}`}>
                <div className="h-16 w-full relative" style={{ backgroundColor: c.hex || '#ffffff' }}>
                  {!RO && (
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(c.hex) ? c.hex : '#000000'} onChange={(e) => setColor(c.id, { hex: e.target.value })} onBlur={() => dirty && persist(kit)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" title="Cambiar color" />
                  )}
                </div>
                <div className="p-2 space-y-1">
                  {RO ? <p className="text-xs font-medium truncate">{c.nombre || 'Color'}</p> : (
                    <Input value={c.nombre} onChange={(e) => setColor(c.id, { nombre: e.target.value })} onBlur={() => dirty && persist(kit)} placeholder="Nombre" className="h-7 text-xs" />
                  )}
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyHex(c.hex)} className="flex-1 inline-flex items-center justify-between gap-1 text-[11px] font-mono px-2 py-1 rounded bg-muted hover:bg-muted/70" data-testid={`bk-copy-hex-${c.id}`}>
                      <span className="uppercase">{c.hex || '—'}</span>
                      {copiedHex === c.hex ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 opacity-60" />}
                    </button>
                    {!RO && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => delColor(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 3) TIPOGRAFÍAS */}
      <SectionCard icon={Type} title="3) Tipografías" desc="Fuente + para qué se usa + enlace o archivo de la fuente."
        action={!RO && <Button size="sm" variant="outline" onClick={addTipo} data-testid="bk-add-tipo"><Plus className="w-4 h-4 mr-1" /> Añadir tipografía</Button>}>
        {kit.tipografias.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin tipografías.</p> : (
          <div className="space-y-3">
            {kit.tipografias.map((t) => (
              <div key={t.id} className="rounded-lg border p-3 space-y-2" data-testid={`bk-tipo-${t.id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Nombre</Label><Input value={t.nombre} onChange={(e) => setTipo(t.id, { nombre: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} placeholder="Ej: Montserrat" /></div>
                  <div><Label className="text-xs">Uso</Label><Input value={t.uso} onChange={(e) => setTipo(t.id, { uso: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} placeholder="Titulares / Textos…" /></div>
                </div>
                <div><Label className="text-xs">Enlace de descarga (Google Fonts, etc.)</Label><Input value={t.url} onChange={(e) => setTipo(t.id, { url: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} placeholder="https://…" /></div>
                <div className="flex items-center gap-2 flex-wrap">
                  {t.file && <DownloadBtn file={t.file} />}
                  {t.url && <a href={t.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir enlace</Button></a>}
                  {!RO && (
                    <>
                      <input ref={(el) => (hiddenInputs.current[`tipo-${t.id}`] = el)} type="file" className="hidden" onChange={(e) => doUpload(`tipo-${t.id}`, e.target.files?.[0], (ref) => ({ ...kit, tipografias: kit.tipografias.map((x) => x.id === t.id ? { ...x, file: ref } : x) }))} />
                      <Button type="button" variant="outline" size="sm" onClick={() => pick(`tipo-${t.id}`)} disabled={uploadingKey === `tipo-${t.id}`}>
                        {uploadingKey === `tipo-${t.id}` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Subir fuente
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 ml-auto" onClick={() => delTipo(t.id)}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 4) PLANTILLAS DE CANVA */}
      <SectionCard icon={LayoutTemplate} title="4) Plantillas de Canva" desc="Enlace a la plantilla real (no una captura). Varios enlaces con nombre; captura opcional."
        action={!RO && <Button size="sm" variant="outline" onClick={addCanva} data-testid="bk-add-canva"><Plus className="w-4 h-4 mr-1" /> Añadir plantilla</Button>}>
        {kit.canva_links.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin plantillas.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kit.canva_links.map((c) => (
              <div key={c.id} className="rounded-lg border p-3 space-y-2" data-testid={`bk-canva-${c.id}`}>
                <div><Label className="text-xs">Nombre (posts, stories, reels…)</Label><Input value={c.nombre} onChange={(e) => setCanva(c.id, { nombre: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} placeholder="Plantillas de posts" /></div>
                <div><Label className="text-xs">Enlace de Canva</Label><Input value={c.url} onChange={(e) => setCanva(c.id, { url: e.target.value })} onBlur={() => dirty && persist(kit)} disabled={RO} placeholder="https://www.canva.com/…" data-testid={`bk-canva-url-${c.id}`} /></div>
                {c.captura && <FilePreview file={c.captura} className="rounded-md border object-cover w-full h-28" />}
                <div className="flex items-center gap-2 flex-wrap">
                  {c.url && <a href={c.url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir plantilla</Button></a>}
                  {!RO && (
                    <>
                      <input ref={(el) => (hiddenInputs.current[`canva-${c.id}`] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadCanvaCaptura(c.id, e.target.files?.[0]); e.target.value = ''; }} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => pick(`canva-${c.id}`)} disabled={uploadingKey === `canva-${c.id}`}>
                        {uploadingKey === `canva-${c.id}` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Captura + colores
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 ml-auto" onClick={() => delCanva(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 5) EJEMPLOS DE CREATIVIDADES */}
      <SectionCard icon={Sparkles} title="5) Ejemplos de creatividades" desc="Galería de referencia del estilo. Nota opcional de por qué funciona."
        action={!RO && (
          <>
            <input ref={(el) => (hiddenInputs.current['ejemplo'] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => doUpload('ejemplo', e.target.files?.[0], (ref) => ({ ...kit, ejemplos: [...kit.ejemplos, { id: uid(), file: ref, nota: '' }] }))} data-testid="bk-ejemplo-file" />
            <Button size="sm" variant="outline" onClick={() => pick('ejemplo')} disabled={uploadingKey === 'ejemplo'} data-testid="bk-add-ejemplo">
              {uploadingKey === 'ejemplo' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Añadir ejemplo
            </Button>
          </>
        )}>
        {kit.ejemplos.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin ejemplos.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {kit.ejemplos.map((e) => (
              <div key={e.id} className="rounded-lg border p-2 space-y-1" data-testid={`bk-ejemplo-${e.id}`}>
                <FilePreview file={e.file} className="rounded-md border object-cover w-full h-32" />
                {RO ? (e.nota && <p className="text-[11px] text-muted-foreground">{e.nota}</p>) : (
                  <Textarea rows={2} value={e.nota} onChange={(ev) => setEjemplo(e.id, { nota: ev.target.value })} onBlur={() => dirty && persist(kit)} placeholder="Por qué funciona…" className="text-xs" />
                )}
                {!RO && <Button variant="ghost" size="sm" className="w-full text-red-500 h-7" onClick={() => delEjemplo(e.id)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar</Button>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 6) MANUAL DE MARCA */}
      <SectionCard icon={FileText} title="6) Manual de marca" desc="PDF del manual de marca (si lo tiene).">
        <div className="flex items-center gap-3 flex-wrap">
          {kit.manual ? <DownloadBtn file={kit.manual} /> : <span className="text-sm text-muted-foreground italic">Sin manual subido.</span>}
          {!RO && (
            <>
              <input ref={(el) => (hiddenInputs.current['manual'] = el)} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => doUpload('manual', e.target.files?.[0], (ref) => ({ ...kit, manual: ref }))} data-testid="bk-manual-file" />
              <Button type="button" variant="outline" size="sm" onClick={() => pick('manual')} disabled={uploadingKey === 'manual'} data-testid="bk-upload-manual">
                {uploadingKey === 'manual' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {kit.manual ? 'Reemplazar PDF' : 'Subir PDF'}
              </Button>
              {kit.manual && <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { const next = { ...kit, manual: null }; setKit(next); persist(next); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar</Button>}
            </>
          )}
        </div>
      </SectionCard>

      {/* 7) USOS INCORRECTOS */}
      <SectionCard icon={Ban} title="7) Usos incorrectos (qué NO hacer)" desc="Evita errores que luego hay que corregir en revisión interna.">
        <div className="space-y-3">
          {RO ? (
            kit.usos_incorrectos?.texto ? <p className="text-sm whitespace-pre-wrap">{kit.usos_incorrectos.texto}</p> : <p className="text-sm text-muted-foreground italic">Sin indicaciones.</p>
          ) : (
            <Textarea rows={4} value={kit.usos_incorrectos?.texto || ''} onChange={(e) => update({ usos_incorrectos: { ...kit.usos_incorrectos, texto: e.target.value } })} onBlur={() => dirty && persist(kit)} placeholder="No deformar el logo, no usar el color X sobre fondo Y…" data-testid="bk-usos-texto" />
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(kit.usos_incorrectos?.imagenes || []).map((f) => (
              <div key={f.id} className="space-y-1" data-testid={`bk-uso-img-${f.id}`}>
                <FilePreview file={f} className="rounded-md border object-cover w-full h-28" />
                {!RO && <Button variant="ghost" size="sm" className="w-full text-red-500 h-7" onClick={() => delUsoImg(f.id)}><Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar</Button>}
              </div>
            ))}
          </div>
          {!RO && (
            <>
              <input ref={(el) => (hiddenInputs.current['uso'] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => doUpload('uso', e.target.files?.[0], (ref) => ({ ...kit, usos_incorrectos: { ...kit.usos_incorrectos, imagenes: [...(kit.usos_incorrectos?.imagenes || []), ref] } }))} data-testid="bk-uso-file" />
              <Button type="button" variant="outline" size="sm" onClick={() => pick('uso')} disabled={uploadingKey === 'uso'}>
                {uploadingKey === 'uso' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Añadir ejemplo de lo que NO hacer
              </Button>
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
